import type { L10n } from '../types'
import type {
  IntegrationCapability,
  IntegrationProviderId,
  IntegrationStatus,
} from '../types/integrations'

export type NotificationGroup = 'health' | 'progress' | 'social' | 'product'

export interface NotificationPref {
  id: string
  group: NotificationGroup
  label: L10n
  hint?: L10n
  defaultOn: boolean
}

export const notificationPrefs: NotificationPref[] = [
  { id: 'dailyCheck', group: 'health', label: { 'pt-BR': 'Lembrete do Daily Check', 'en-US': 'Daily Check reminder' }, hint: { 'pt-BR': 'Uma vez por dia, no horário que você costuma registrar.', 'en-US': 'Once a day, around the time you usually check in.' }, defaultOn: true },
  { id: 'hydration', group: 'health', label: { 'pt-BR': 'Hidratação', 'en-US': 'Hydration' }, defaultOn: true },
  { id: 'sleep', group: 'health', label: { 'pt-BR': 'Sono', 'en-US': 'Sleep' }, defaultOn: true },
  { id: 'habits', group: 'health', label: { 'pt-BR': 'Hábitos pendentes', 'en-US': 'Pending habits' }, defaultOn: true },
  { id: 'weeklyReview', group: 'health', label: { 'pt-BR': 'Revisão Semanal', 'en-US': 'Weekly Review' }, defaultOn: true },

  { id: 'streak', group: 'progress', label: { 'pt-BR': 'Aviso de sequência', 'en-US': 'Streak warning' }, defaultOn: true },
  { id: 'xp', group: 'progress', label: { 'pt-BR': 'Marcos de Bee', 'en-US': 'Bee milestones' }, defaultOn: true },
  { id: 'badges', group: 'progress', label: { 'pt-BR': 'Medalhas', 'en-US': 'Badges' }, defaultOn: true },
  { id: 'goals', group: 'progress', label: { 'pt-BR': 'Metas', 'en-US': 'Goals' }, defaultOn: false },

  { id: 'likes', group: 'social', label: { 'pt-BR': 'Curtidas', 'en-US': 'Likes' }, defaultOn: false },
  { id: 'comments', group: 'social', label: { 'pt-BR': 'Comentários', 'en-US': 'Comments' }, defaultOn: true },
  { id: 'followers', group: 'social', label: { 'pt-BR': 'Novos seguidores', 'en-US': 'New followers' }, defaultOn: false },
  { id: 'mentions', group: 'social', label: { 'pt-BR': 'Menções', 'en-US': 'Mentions' }, defaultOn: true },

  { id: 'updates', group: 'product', label: { 'pt-BR': 'Atualizações importantes', 'en-US': 'Important updates' }, hint: { 'pt-BR': 'Mudanças que afetam sua conta ou seus dados.', 'en-US': 'Changes that affect your account or your data.' }, defaultOn: true },
]

export interface PrivacyPref {
  id: string
  label: L10n
  hint: L10n
  defaultOn: boolean
  /** Health data requires explicit consent and starts off. */
  health?: boolean
  /**
   * 'account' = who can reach you; 'public' = what your public profile shows.
   * Every item that can appear publicly MUST exist here with its own toggle.
   */
  scope: 'account' | 'public'
  /** Public item only shown when this parent toggle is also on. */
  parent?: string
  /** Category whose individual metrics are selected one by one. */
  granular?: 'sport' | 'body' | 'biomarker'
}

export const privacyPrefs: PrivacyPref[] = [
  { id: 'publicProfile', scope: 'account', label: { 'pt-BR': 'Perfil público', 'en-US': 'Public profile' }, hint: { 'pt-BR': 'Qualquer pessoa pode ver seu perfil e seus posts.', 'en-US': 'Anyone can see your profile and posts.' }, defaultOn: true },
  { id: 'followerRequests', scope: 'account', label: { 'pt-BR': 'Aceitar pedidos de seguir', 'en-US': 'Allow follower requests' }, hint: { 'pt-BR': 'Com o perfil privado, novos seguidores passam por aprovação.', 'en-US': 'With a private profile, new followers need approval.' }, defaultOn: true },
  { id: 'publicPosts', scope: 'account', label: { 'pt-BR': 'Posts públicos', 'en-US': 'Public posts' }, hint: { 'pt-BR': 'Seus posts aparecem na Comunidade.', 'en-US': 'Your posts appear in Community.' }, defaultOn: true },
  { id: 'pulseVisibility', scope: 'account', label: { 'pt-BR': 'Pulse visível para seguidores', 'en-US': 'Pulse visible to followers' }, hint: { 'pt-BR': 'Pulses somem quando deixam de estar ativos.', 'en-US': 'Pulses disappear once they stop being active.' }, defaultOn: true },

  /*
   * PUBLIC PROFILE — "O que aparece no meu perfil público".
   * Default ON: achievements, badges, streak.
   * Default OFF: sports performance, progress, body metrics, biomarkers and
   * ANY future health or performance category. Categories with `granular`
   * also need each individual metric selected before it is shown.
   */
  { id: 'publicAchievements', scope: 'public', label: { 'pt-BR': 'Conquistas', 'en-US': 'Achievements' }, hint: { 'pt-BR': 'Mostrar minhas conquistas no perfil público.', 'en-US': 'Show my achievements on my public profile.' }, defaultOn: true },
  { id: 'publicBadges', scope: 'public', parent: 'publicAchievements', label: { 'pt-BR': 'Medalhas', 'en-US': 'Badges' }, hint: { 'pt-BR': 'Primeiro Check, Foco Total, Semana de Ferro…', 'en-US': 'First Check, Total Focus, Iron Week…' }, defaultOn: true },
  { id: 'publicStreak', scope: 'public', label: { 'pt-BR': 'Sequência', 'en-US': 'Streak' }, hint: { 'pt-BR': 'Dias seguidos com o Daily Check completo.', 'en-US': 'Consecutive days with a complete Daily Check.' }, defaultOn: true },
  { id: 'publicSportMetrics', scope: 'public', granular: 'sport', label: { 'pt-BR': 'Performance esportiva', 'en-US': 'Sports performance' }, hint: { 'pt-BR': 'Desligado por padrão. Depois, escolha cada métrica.', 'en-US': 'Off by default. Then choose each metric.' }, defaultOn: false },
  { id: 'publicProgress', scope: 'public', label: { 'pt-BR': 'Métricas de progresso', 'en-US': 'Progress metrics' }, hint: { 'pt-BR': 'Média do Life Score em 30 dias. Desligado por padrão.', 'en-US': '30-day Life Score average. Off by default.' }, defaultOn: false },
  { id: 'publicBodyMetrics', scope: 'public', health: true, granular: 'body', label: { 'pt-BR': 'Métricas corporais', 'en-US': 'Body metrics' }, hint: { 'pt-BR': 'Composição corporal e BIA. Exige seu consentimento explícito; depois, escolha cada métrica.', 'en-US': 'Body composition and BIA. Requires your explicit consent; then choose each metric.' }, defaultOn: false },
  { id: 'publicBiomarkers', scope: 'public', health: true, granular: 'biomarker', label: { 'pt-BR': 'Biomarcadores', 'en-US': 'Biomarkers' }, hint: { 'pt-BR': 'Resultados de exames, como testosterona. Exige seu consentimento explícito; depois, escolha cada biomarcador.', 'en-US': 'Lab results such as testosterone. Requires your explicit consent; then choose each biomarker.' }, defaultOn: false },
]

export interface AccessibilityPref {
  id: 'reduceMotion' | 'increaseContrast' | 'largerText' | 'haptics'
  label: L10n
  hint: L10n
  defaultOn: boolean
}

export const accessibilityPrefs: AccessibilityPref[] = [
  { id: 'reduceMotion', label: { 'pt-BR': 'Reduzir movimento', 'en-US': 'Reduce motion' }, hint: { 'pt-BR': 'Desliga transições e animações não essenciais.', 'en-US': 'Turns off non-essential transitions and animations.' }, defaultOn: false },
  { id: 'increaseContrast', label: { 'pt-BR': 'Aumentar contraste', 'en-US': 'Increase contrast' }, hint: { 'pt-BR': 'Bordas e textos secundários mais fortes.', 'en-US': 'Stronger borders and secondary text.' }, defaultOn: false },
  { id: 'largerText', label: { 'pt-BR': 'Texto maior', 'en-US': 'Larger text' }, hint: { 'pt-BR': 'Compatível com o tamanho de fonte do sistema.', 'en-US': 'Compatible with the system font size.' }, defaultOn: false },
  { id: 'haptics', label: { 'pt-BR': 'Retorno tátil', 'en-US': 'Haptic feedback' }, hint: { 'pt-BR': 'Vibração curta ao concluir hábitos e marcos.', 'en-US': 'Short vibration when completing habits and milestones.' }, defaultOn: true },
]

export interface ConnectedService {
  id: IntegrationProviderId
  /** Brand name. Generic entries use `label` instead. */
  name: string
  label?: L10n
  description: L10n
  /** Every provider is `comingSoon` until a real integration exists. */
  status: IntegrationStatus
  /** What the provider is planned to support. Not active today. */
  capabilities: IntegrationCapability[]
}

export const connectedServices: ConnectedService[] = [
  { id: 'apple-health', name: 'Apple Health', description: { 'pt-BR': 'Passos, sono e treinos do iPhone e do Apple Watch.', 'en-US': 'Steps, sleep and workouts from iPhone and Apple Watch.' }, status: 'comingSoon', capabilities: ['importActivities', 'importHealth'] },
  { id: 'google-health', name: 'Google Health Connect', description: { 'pt-BR': 'Dados de saúde de apps Android compatíveis.', 'en-US': 'Health data from compatible Android apps.' }, status: 'comingSoon', capabilities: ['importActivities', 'importHealth'] },
  { id: 'garmin', name: 'Garmin', description: { 'pt-BR': 'Treinos, pace e frequência cardíaca.', 'en-US': 'Workouts, pace and heart rate.' }, status: 'comingSoon', capabilities: ['importActivities'] },
  { id: 'strava', name: 'Strava', description: { 'pt-BR': 'Atividades de corrida, ciclismo e natação.', 'en-US': 'Running, cycling and swimming activities.' }, status: 'comingSoon', capabilities: ['importActivities', 'crossPost'] },
  { id: 'wearables', name: 'Wearables', description: { 'pt-BR': 'Pulseiras e balanças de bioimpedância.', 'en-US': 'Bands and bioimpedance scales.' }, status: 'comingSoon', capabilities: ['importActivities', 'importHealth'] },
  { id: 'other', name: 'Other', label: { 'pt-BR': 'Outras plataformas', 'en-US': 'Other platforms' }, description: { 'pt-BR': 'Outros apps de treino e bem-estar.', 'en-US': 'Other fitness and wellness apps.' }, status: 'comingSoon', capabilities: ['importActivities', 'crossPost'] },
]

export interface SessionEntry {
  id: string
  device: string
  place: L10n
  lastActive: L10n
  current: boolean
  trusted: boolean
}

export const sessions: SessionEntry[] = [
  { id: 's1', device: 'iPhone 15 · Beelyv iOS', place: { 'pt-BR': 'São Paulo, BR', 'en-US': 'São Paulo, BR' }, lastActive: { 'pt-BR': 'agora', 'en-US': 'now' }, current: true, trusted: true },
  { id: 's2', device: 'MacBook Air · Safari', place: { 'pt-BR': 'São Paulo, BR', 'en-US': 'São Paulo, BR' }, lastActive: { 'pt-BR': 'há 2 dias', 'en-US': '2 days ago' }, current: false, trusted: true },
  { id: 's3', device: 'Pixel 8 · Chrome', place: { 'pt-BR': 'Campinas, BR', 'en-US': 'Campinas, BR' }, lastActive: { 'pt-BR': 'há 3 semanas', 'en-US': '3 weeks ago' }, current: false, trusted: false },
]

export interface LoginEvent {
  id: string
  label: L10n
  when: L10n
  ok: boolean
}

export const loginActivity: LoginEvent[] = [
  { id: 'l1', label: { 'pt-BR': 'Entrada no iPhone 15', 'en-US': 'Sign-in on iPhone 15' }, when: { 'pt-BR': 'hoje, 06:42', 'en-US': 'today, 6:42am' }, ok: true },
  { id: 'l2', label: { 'pt-BR': 'Entrada no Safari (macOS)', 'en-US': 'Sign-in on Safari (macOS)' }, when: { 'pt-BR': 'há 2 dias, 21:10', 'en-US': '2 days ago, 9:10pm' }, ok: true },
  { id: 'l3', label: { 'pt-BR': 'Tentativa recusada no Chrome', 'en-US': 'Declined attempt on Chrome' }, when: { 'pt-BR': 'há 3 semanas, 23:58', 'en-US': '3 weeks ago, 11:58pm' }, ok: false },
]

export interface ConsentEvent {
  id: string
  label: L10n
  when: L10n
  granted: boolean
}

export const consentHistory: ConsentEvent[] = [
  { id: 'c1', label: { 'pt-BR': 'Termos de uso aceitos', 'en-US': 'Terms of use accepted' }, when: { 'pt-BR': '12 de junho de 2026', 'en-US': 'June 12, 2026' }, granted: true },
  { id: 'c2', label: { 'pt-BR': 'Política de privacidade aceita', 'en-US': 'Privacy policy accepted' }, when: { 'pt-BR': '12 de junho de 2026', 'en-US': 'June 12, 2026' }, granted: true },
  { id: 'c3', label: { 'pt-BR': 'Publicação de métricas corporais', 'en-US': 'Publishing body metrics' }, when: { 'pt-BR': 'nunca concedido', 'en-US': 'never granted' }, granted: false },
]

export interface FaqEntry {
  id: string
  question: L10n
  answer: L10n
}

export const faq: FaqEntry[] = [
  { id: 'f1', question: { 'pt-BR': 'Como o Life Score é calculado?', 'en-US': 'How is the Life Score calculated?' }, answer: { 'pt-BR': 'O Life Score transforma os resultados do seu Daily Check em uma escala de bem-estar de 0 a 100. O bônus da Foto do Dia não entra no cálculo.', 'en-US': 'Life Score translates your Daily Check results into a wellbeing scale from 0 to 100. The Photo of the Day bonus is not counted.' } },
  { id: 'f2', question: { 'pt-BR': 'Por que o Post completo precisa de 50 Bee?', 'en-US': 'Why does the full Post need 50 Bee?' }, answer: { 'pt-BR': 'Porque o BEELYV coloca autocuidado antes da vitrine. O Pulse continua livre a qualquer momento.', 'en-US': 'Because Beelyv puts self-care before the showcase. Pulse stays free at any time.' } },
  { id: 'f3', question: { 'pt-BR': 'Minhas métricas corporais são públicas?', 'en-US': 'Are my body metrics public?' }, answer: { 'pt-BR': 'Não. Composição corporal e dados de BIA são privados por padrão e só mudam com o seu consentimento explícito.', 'en-US': 'No. Body composition and BIA data are private by default and only change with your explicit consent.' } },
  { id: 'f4', question: { 'pt-BR': 'O que acontece se eu perder um dia?', 'en-US': 'What happens if I miss a day?' }, answer: { 'pt-BR': 'A sequência zera, mas o histórico e as medalhas conquistadas permanecem.', 'en-US': 'The streak resets, but your history and earned badges remain.' } },
]

export const APP_VERSION = '1.0.0 (protótipo)'
